import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Spinner, Alert, Card, Row, Col, Pagination } from 'react-bootstrap';
import { FaSearch, FaRedo } from 'react-icons/fa';
import { getAllUsers, getCurrentUser,downloadAllUsers } from 'store/auth';
import { useAuth } from 'components/authentication/signup-login/AuthContext';
import { FaDownload } from "react-icons/fa";
const UsersTab = () => {
  const { user, isAuthenticated } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isFetchingUser, setIsFetchingUser] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [limit, setLimit] = useState(10);
  
  // Filter state - single search field
  const [searchQuery, setSearchQuery] = useState('');
  const [tempSearchQuery, setTempSearchQuery] = useState('');

  // Fetch current user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!isAuthenticated) return;
      
      setIsFetchingUser(true);
      try {
        const response = await getCurrentUser();
        const userData = response.data?.user;
        
        if (userData) {
          setCurrentUser(userData);
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
      } finally {
        setIsFetchingUser(false);
      }
    };

    fetchUserData();
  }, [isAuthenticated]);

  // Fallback to context user if API call hasn't completed
  const displayUser = currentUser || user;

  // Fetch users function
  const fetchUsers = async (page = 1, query = searchQuery) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getAllUsers({
        page,
        limit,
        search: query || undefined
      });
      
      // Match the EXACT same pattern as ScreeningTab.jsx
      setUsers(response.data || []);
      setTotalUsers(response.total || 0);
      setCurrentPage(response.page || 1);
      setTotalPages(Math.ceil((response.total || 0) / limit));
      console.error("users:", response.data);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to fetch users');
      setUsers([]);
      setTotalUsers(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchUsers(1, searchQuery);
  }, [limit]);

  // Apply search
  const handleApplySearch = () => {
    setSearchQuery(tempSearchQuery);
    setCurrentPage(1);
    fetchUsers(1, tempSearchQuery);
  };

  // Reset search
  const handleResetSearch = () => {
    setTempSearchQuery('');
    setSearchQuery('');
    setCurrentPage(1);
    fetchUsers(1, '');
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplySearch();
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchUsers(page, searchQuery);
  };

  // Handle limit change
  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Generate pagination items
  const renderPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  return (
    <div>
      {/* Search Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <h5 className="mb-3">Search Users</h5>
          <div className="d-flex justify-content-between align-items-end">
            <div className="d-flex gap-2 align-items-end" style={{ flex: 1 }}>
              <Form.Group style={{ flex: 1, maxWidth: "700px" }}>
                <Form.Label className="small fw-semibold">
                  <FaSearch className="me-1" />
                  Search by Name or Email
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter name or email to search..."
                  value={tempSearchQuery}
                  onChange={(e) => setTempSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
              </Form.Group>
              <Button
                variant="primary"
                onClick={handleApplySearch}
                className="d-flex align-items-center px-3"
                style={{ minHeight: "38px" }}
                disabled={loading}
              >
                Search      
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleResetSearch}
                disabled={loading}
                className="d-flex align-items-center px-3"
                style={{ minHeight: "38px" }}
              >
                Clear
              </Button>
            </div>
            <Button
              variant="success"
              onClick={downloadAllUsers}
              className="d-flex align-items-center px-3"
              style={{ minHeight: "38px" }}
              disabled={loading}
            >
              <FaDownload className="me-2" />
              Export All
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Results Summary */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0">Users List</h5>
          <small className="text-muted">
            Showing {users.length} of {totalUsers} users
          </small>
        </div>
        <div className="d-flex align-items-center gap-3">
          <Form.Group className="d-flex align-items-center mb-0">
            <Form.Label className="mb-0 me-2 small">Rows per page:</Form.Label>
            <Form.Select
              size="sm"
              value={limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              style={{ width: 'auto' }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </Form.Group>
        </div>
      </div>

      {/* Users Table */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-users fa-3x text-muted mb-3"></i>
              <p className="text-muted">No users found</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="px-3 py-3">ID</th>
                    <th className="px-3 py-3">Name</th>
                    <th className="px-3 py-3">Email</th>
                    <th className="px-3 py-3">User Type</th>
                    <th className="px-3 py-3">Created At</th>
                    <th className="px-3 py-3">Verified</th>
                    <th className="px-3 py-3">Agree To Receive <br/>News & Updates</th>
                    <th className="px-3 py-3">Admin</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-3 py-3">{user.id}</td>
                      <td className="px-3 py-3">
                        <div className="fw-semibold">{user.name}</div>
                      </td>
                      <td className="px-3 py-3">{user.email}</td>
                      <td className="px-3 py-3">
                        <span className="px-3 py-3">
                          {user.usertype}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-muted small">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-3 py-3">
                        {user.is_verified === true ? (
                          <div className="d-flex align-items-center">
                            <i className="fas fa-check-circle me-2" style={{ color: "#0d6efd", fontSize: "18px" }}></i>
                            <span className="text-success fw-">Verified</span>
                          </div>
                        ) : (
                          <div className="d-flex align-items-center">
                            <i className="fas fa-times-circle me-2" style={{ color: "red", fontSize: "18px" }}></i>
                            <span className="text-danger fw-">Not Verified</span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center small">
                        {user.agreetonews ? 'Yes' : 'No'}
                      </td>
                      <td className="px-3 py-3">
                        {user.is_admin === true ? (
                          <span style={{ color: "black", fontSize: "10px" }}>✔</span>

                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {!loading && users.length > 0 && totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <Pagination className="mb-0">
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {renderPaginationItems()}
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default UsersTab;